import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, ChevronLeft, ChevronRight, List, Check } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/*
 * "Light as Language" Ebook Reader
 * Dark sky. Glowing text. Constellations of meaning.
 * Now with reading-progress tracking.
 */

const READER_CSS = `
.light-reader { font-family: 'Georgia', 'Times New Roman', serif; }
.light-reader .body-text { color: #f0ddb0; line-height: 2.1; margin-bottom: 1.2em; font-size: 0.95rem; }
.light-reader .body-text.text-center { text-align: center; }
.light-reader .body-text.text-right { text-align: right; }
.light-reader .section-heading {
  color: #fcd34d; font-weight: 700; font-size: 1.15rem; text-align: center;
  letter-spacing: 0.1em; margin: 2em 0 1em; padding: 0.8em 0;
  border-top: 1px solid rgba(200,165,90,0.2); border-bottom: 1px solid rgba(200,165,90,0.2);
}
.light-reader .glow-text { transition: text-shadow 0.3s; }
.light-reader .glow-text:hover { filter: brightness(1.3); }
.light-reader .table-wrapper {
  margin: 2em 0; overflow-x: auto; border-radius: 8px;
  border: 1px solid rgba(200,165,90,0.2);
  box-shadow: 0 0 30px rgba(200,165,90,0.06), 0 8px 24px rgba(0,0,0,0.3);
}
.light-reader .rich-table {
  width: 100%; border-collapse: collapse; font-size: 0.85rem; line-height: 1.8;
}
.light-reader .rich-table td {
  vertical-align: top; border: 1px solid rgba(200,165,90,0.12) !important;
}
.light-reader .rich-table tr:first-child td {
  border-top: none !important; font-weight: 600;
}
@keyframes starPulse { 0%, 100% { text-shadow: 0 0 8px currentColor; } 50% { text-shadow: 0 0 16px currentColor, 0 0 30px currentColor; } }
.light-reader .glow-text { animation: starPulse 4s ease-in-out infinite; }
.light-reader .glow-text:nth-child(2n) { animation-delay: 1s; }
.light-reader .glow-text:nth-child(3n) { animation-delay: 2s; }
.light-reader::-webkit-scrollbar { width: 6px; }
.light-reader::-webkit-scrollbar-track { background: #060503; }
.light-reader::-webkit-scrollbar-thumb { background: rgba(200,165,90,0.2); border-radius: 3px; }
.light-reader::-webkit-scrollbar-thumb:hover { background: rgba(200,165,90,0.4); }
`;

const DynamicEbookReader = () => {
  const { bookId, chapterNum } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, token } = useAuth();
  const [book, setBook] = useState(null);
  const [chapters, setChapters] = useState([]);
  const [currentChapter, setCurrentChapter] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showTOC, setShowTOC] = useState(false);
  const [scrollPct, setScrollPct] = useState(0);
  const [chaptersRead, setChaptersRead] = useState([]);
  const contentRef = useRef(null);
  const saveTimerRef = useRef(null);

  useEffect(() => {
    if (!isAuthenticated()) { navigate('/login'); return; }
    fetchBook();
  }, [bookId]);

  useEffect(() => {
    if (chapters.length > 0) {
      const order = parseInt(chapterNum) || 1;
      const idx = Math.max(0, Math.min(order - 1, chapters.length - 1));
      setCurrentIndex(idx);
      setCurrentChapter(chapters[idx]);
      setScrollPct(0);
      if (contentRef.current) contentRef.current.scrollTop = 0;
    }
  }, [chapterNum, chapters]);

  // Fetch reading progress after book loads
  useEffect(() => {
    if (book && bookId) fetchProgress();
  }, [book, bookId]);

  const fetchBook = async () => {
    try {
      const headers = {};
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const res = await fetch(`${API_URL}/api/books/${bookId}`, { headers, credentials: 'include' });
      if (!res.ok) {
        if (res.status === 403) throw new Error('Subscription required');
        if (res.status === 404) throw new Error('This book is not yet available');
        if (res.status === 401) throw new Error('Please sign in to read this book');
        throw new Error('Failed to load');
      }
      const data = await res.json();
      setBook(data.book);
      setChapters(data.chapters || []);
    } catch (e) {
      setError(e.message);
    }
    setLoading(false);
  };

  const fetchProgress = async () => {
    try {
      const headers = {};
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const res = await fetch(`${API_URL}/api/reading/progress/${bookId}`, { headers, credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setChaptersRead(data.chapters_read || []);
      }
    } catch (_) { /* silent */ }
  };

  const saveProgress = useCallback((chapterNumber, pct) => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(async () => {
      try {
        const headers = { 'Content-Type': 'application/json' };
        if (token) headers['Authorization'] = `Bearer ${token}`;
        await fetch(`${API_URL}/api/reading/progress`, {
          method: 'POST',
          headers,
          credentials: 'include',
          body: JSON.stringify({ book_id: bookId, chapter_number: chapterNumber, scroll_pct: pct }),
        });
        // Log reading streak (fire-and-forget)
        fetch(`${API_URL}/api/reading-streaks/log`, {
          method: 'POST',
          headers,
          credentials: 'include',
        }).catch(() => {});
        // Mark this chapter as read if scrolled past 50%
        if (pct > 50 && !chaptersRead.includes(chapterNumber)) {
          setChaptersRead(prev => [...new Set([...prev, chapterNumber])]);
        }
      } catch (_) { /* silent */ }
    }, 800);
  }, [token, bookId, chaptersRead]);

  // Handle scroll to track progress
  const handleScroll = useCallback(() => {
    const el = contentRef.current;
    if (!el) return;
    const pct = el.scrollHeight <= el.clientHeight ? 100
      : Math.round((el.scrollTop / (el.scrollHeight - el.clientHeight)) * 100);
    setScrollPct(pct);
    if (currentChapter) {
      saveProgress(currentChapter.chapter_number || currentIndex + 1, pct);
    }
  }, [currentChapter, currentIndex, saveProgress]);

  useEffect(() => {
    const el = contentRef.current;
    if (el) {
      el.addEventListener('scroll', handleScroll, { passive: true });
      return () => el.removeEventListener('scroll', handleScroll);
    }
  }, [handleScroll]);

  const goToChapter = (idx) => {
    const newIdx = Math.max(0, Math.min(idx, chapters.length - 1));
    setCurrentIndex(newIdx);
    setCurrentChapter(chapters[newIdx]);
    navigate(`/ebooks/${bookId}/${newIdx + 1}`, { replace: true });
    if (contentRef.current) contentRef.current.scrollTop = 0;
    setShowTOC(false);
    setScrollPct(0);
  };

  const hasRichContent = currentChapter?.rich_content && currentChapter.rich_content.length > 10;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen" style={{ background: '#060503' }}>
        <div className="text-center">
          <div className="w-10 h-10 border-2 border-amber-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-amber-200/50 font-serif text-sm">Opening the scroll...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-screen" style={{ background: '#060503' }}>
        <div className="text-center max-w-md px-6">
          <p className="text-amber-400 font-serif text-lg mb-4">{error}</p>
          <button onClick={() => navigate('/library')} className="text-amber-600 hover:text-amber-400 font-serif text-sm underline">
            Return to Library
          </button>
        </div>
      </div>
    );
  }

  const currentChNum = currentChapter?.chapter_number || currentIndex + 1;

  return (
    <>
      <style>{READER_CSS}</style>
      <div className="h-screen flex flex-col" style={{ background: '#060503' }} data-testid="ebook-reader">

        {/* Scroll progress bar — thin amber line at top */}
        <div className="w-full h-[2px]" style={{ background: 'rgba(200,165,90,0.06)' }} data-testid="progress-bar-track">
          <div
            className="h-full transition-all duration-300"
            style={{
              width: `${scrollPct}%`,
              background: 'linear-gradient(90deg, rgba(200,165,90,0.3), rgba(252,211,77,0.7))',
              boxShadow: scrollPct > 0 ? '0 0 8px rgba(252,211,77,0.3)' : 'none',
            }}
            data-testid="progress-bar-fill"
          />
        </div>

        {/* Top bar */}
        <div className="flex items-center justify-between px-4 py-3 border-b" style={{ borderColor: 'rgba(200,165,90,0.15)', background: '#060503' }}>
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/library')} data-testid="btn-back-library"
              className="text-amber-500 hover:text-amber-300 transition-colors p-1.5">
              <ArrowLeft className="w-4 h-4" />
            </button>
            <div>
              <p className="text-[10px] tracking-[0.3em] uppercase text-amber-500/80 font-mono">{book?.title}</p>
              <p className="text-xs text-amber-300 font-serif">{currentChapter?.title}</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {/* Scroll percentage */}
            <span className="text-[10px] text-amber-500/70 font-mono" data-testid="scroll-pct">{scrollPct}%</span>
            <span className="text-[10px] text-amber-500/70 font-mono">{currentIndex + 1} / {chapters.length}</span>
            <button onClick={() => setShowTOC(!showTOC)} data-testid="btn-toggle-toc"
              className="p-1.5 rounded text-amber-500 hover:text-amber-300 transition-colors">
              <List className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* TOC overlay with read indicators */}
        {showTOC && (
          <div className="absolute inset-0 z-50 flex" style={{ background: 'rgba(6,5,3,0.95)' }}>
            <div className="max-w-lg mx-auto w-full py-8 px-6 overflow-y-auto" data-testid="toc-panel">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-lg font-serif text-amber-400">Contents</h2>
                <button onClick={() => setShowTOC(false)} className="text-amber-700 hover:text-amber-400 text-sm font-serif">Close</button>
              </div>
              {/* Overall progress */}
              <div className="mb-6 px-4">
                <div className="flex items-center justify-between text-[10px] font-mono text-amber-500/70 mb-1">
                  <span>Progress</span>
                  <span>{chaptersRead.length} / {chapters.length} chapters</span>
                </div>
                <div className="w-full h-1 rounded-full" style={{ background: 'rgba(200,165,90,0.08)' }}>
                  <div
                    className="h-full rounded-full transition-all"
                    style={{
                      width: `${chapters.length > 0 ? (chaptersRead.length / chapters.length) * 100 : 0}%`,
                      background: 'linear-gradient(90deg, rgba(200,165,90,0.4), rgba(252,211,77,0.7))',
                    }}
                    data-testid="overall-progress-bar"
                  />
                </div>
              </div>
              <div className="space-y-1">
                {chapters.map((ch, i) => {
                  const chNum = ch.chapter_number || i + 1;
                  const isRead = chaptersRead.includes(chNum);
                  return (
                    <button key={i} onClick={() => goToChapter(i)}
                      data-testid={`toc-ch-${i + 1}`}
                      className={`w-full text-left px-4 py-3 rounded-lg text-sm font-serif transition-colors flex items-center gap-3 ${
                        i === currentIndex
                          ? 'text-amber-200 bg-amber-900/15 border-l-2 border-amber-500'
                          : 'text-amber-400/70 hover:text-amber-300 hover:bg-amber-900/10'
                      }`}>
                      {/* Read indicator */}
                      <span className="flex-shrink-0 w-5 h-5 flex items-center justify-center" data-testid={`toc-read-${i + 1}`}>
                        {isRead ? (
                          <Check className="w-3.5 h-3.5 text-amber-400" />
                        ) : (
                          <span className="w-1.5 h-1.5 rounded-full" style={{ background: i === currentIndex ? 'rgba(252,211,77,0.6)' : 'rgba(200,165,90,0.3)' }} />
                        )}
                      </span>
                      <span>
                        <span className="text-[10px] font-mono text-amber-500/60 mr-2">{i + 1}</span>
                        {ch.title}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Main content */}
        <div ref={contentRef} className="flex-1 overflow-y-auto light-reader" data-testid="reader-content">
          <div className="max-w-2xl mx-auto px-6 sm:px-10 py-12 sm:py-16">
            <div className="text-center mb-12">
              <p className="text-[9px] tracking-[0.5em] uppercase font-mono mb-4" style={{ color: 'rgba(200,165,90,0.6)' }}>
                {book?.title}
              </p>
              <h1 className="text-2xl sm:text-3xl font-serif font-bold mb-3" data-testid="chapter-title"
                style={{ color: '#fcd34d', textShadow: '0 0 40px rgba(252,211,77,0.15)' }}>
                {currentChapter?.title}
              </h1>
              <div className="h-px w-24 mx-auto" style={{ background: 'linear-gradient(90deg, transparent, rgba(200,165,90,0.3), transparent)' }} />
            </div>

            {hasRichContent ? (
              <div data-testid="rich-content" dangerouslySetInnerHTML={{ __html: currentChapter.rich_content }} />
            ) : (
              <div data-testid="plain-content">
                {(currentChapter?.content || '').split('\n').filter(p => p.trim()).map((para, i) => (
                  <p key={i} className="body-text" style={{ color: '#f0ddb0' }}>{para}</p>
                ))}
              </div>
            )}

            <div className="text-center mt-16 mb-8">
              <div className="h-px w-16 mx-auto mb-4" style={{ background: 'linear-gradient(90deg, transparent, rgba(200,165,90,0.3), transparent)' }} />
              <p className="text-[9px] tracking-[0.3em] uppercase font-mono" style={{ color: 'rgba(200,165,90,0.5)' }}>
                {currentIndex < chapters.length - 1 ? 'Continue' : 'End'}
              </p>
            </div>
          </div>
        </div>

        {/* Bottom nav */}
        <div className="flex items-center justify-between px-4 py-3 border-t" style={{ borderColor: 'rgba(200,165,90,0.15)', background: '#060503' }} data-testid="reader-nav">
          <button onClick={() => goToChapter(currentIndex - 1)}
            disabled={currentIndex <= 0}
            data-testid="btn-prev-chapter"
            className="flex items-center gap-1.5 px-3 py-2 rounded-full text-sm font-serif transition-colors disabled:opacity-20 text-amber-500 hover:text-amber-300">
            <ChevronLeft className="w-4 h-4" /> Prev
          </button>

          <button onClick={() => navigate('/mindmap')} data-testid="btn-to-mindmap"
            className="text-[10px] tracking-[0.15em] uppercase font-mono text-amber-500/60 hover:text-amber-300 transition-colors">
            Mind Map
          </button>

          <button onClick={() => goToChapter(currentIndex + 1)}
            disabled={currentIndex >= chapters.length - 1}
            data-testid="btn-next-chapter"
            className="flex items-center gap-1.5 px-3 py-2 rounded-full text-sm font-serif transition-colors disabled:opacity-20 text-amber-500 hover:text-amber-300">
            Next <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </>
  );
};

export default DynamicEbookReader;
